import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Save, Send, Plus, Trash2, ChevronDown, ChevronRight,
  CheckCircle2, AlertTriangle, Gauge, Loader2, Cloud, Check, MapPin,
} from 'lucide-react';
import FotosItem from './FotosItem';
// Hook compartilhado dentro do módulo Manutenção — o mesmo que a Certificação
// usa nas duas etapas. Importado de lá de propósito, em vez de copiado: já
// existem DUAS cópias idênticas do hook no projeto (certificacao/ e
// operacional/bsv/) e uma terceira só pioraria a conta.
import useAutosaveRascunho from '../../../utils/useAutosaveRascunho';
import useEhCompacto from '../../../utils/useEhCompacto';
// Mapeamento loja → empresa (fonte única do projeto). A empresa NÃO é digitada:
// ela é uma propriedade da loja, e deixá-la livre produziria 'P&M', 'PM',
// 'P & M' na mesma coluna — que é justamente o que quebra agrupamento em
// relatório e BI.
import { getEmpresa, ehLojaManutencao } from '../../../utils/empresas';
// ⚠️ Régua do ICP em arquivo próprio porque é ESPELHO do servidor
// (conserv-icp-utils.js) e há teste travando o par — ver o cabeçalho de lá.
import { classificarIcp } from './conservacao-icp-regras';

// =====================================================================
// Etapa 1 — INSPEÇÃO da Conservação Predial · POR EXCEÇÃO (DEM-00025, Fase A).
//
// ⚠️⚠️ MUDOU O JEITO DE PREENCHER (antes: classificar os 89 itens um a um).
//    Agora o consultor faz o que já fazia no app Report & Run: percorre o
//    prédio, marca EM QUAIS ÁREAS entrou e registra SÓ o que precisa de reparo
//    (o achado). O resto é DERIVADO no servidor —
//
//      item de área visitada SEM achado  → 'Conforme'
//      item de área marcada "não existe"  → 'N/A'  (3º estado — DEM-00049)
//      item de área NÃO visitada          → 'Fora do escopo'  (fora da cobertura)
//
//    Isso já é o contrato que o backend aceita (`competencias`,
//    `competencias_nao_existentes` e `achados`, materializado em
//    conserv-materializar.js). Esta tela passou a FALAR esse contrato — antes
//    ela mandava o antigo (uma linha por item), que caía no gate "há N itens sem
//    resposta".
//
// ⚠️ O 'N/A' VOLTOU À TELA, MAS POR ÁREA INTEIRA (DEM-00049), não por item. O
//    inspetor marca "não existe nesta loja" numa área (ex.: Salão numa loja de
//    shopping): os itens dela viram 'N/A' — FORA do ICP e, ao contrário do
//    'Fora do escopo', SEM derrubar a cobertura ("fui lá e não existe" é um
//    olhar). Item individual que a unidade não tem numa área que EXISTE segue
//    sem se registrar → ganha o ponto como conforme (efeito colateral aceito).
//    A régua do ICP segue binária: todo achado é UMA pendência.
//
// ⚠️ A COBERTURA (quanto do checklist foi olhado) anda colada ao ICP: 89% sobre
//    3 áreas não é a mesma informação que 89% sobre o prédio inteiro. As duas
//    aparecem juntas — é o freio de inspecionar por exceção.
//
// ⚠️ A geração automática de Ordem de Serviço (1 por achado, na finalização)
//    CONTINUA nesta fase — a troca por OS avulsa é a Fase B da DEM-00025.
//
// O ICP mostrado aqui é uma PRÉVIA calculada no navegador; o valor OFICIAL é
// sempre o do servidor, no submit — o cliente nunca decide nota.
// =====================================================================

const API = '/api/manutencao/conservacao';
// A área genérica de quiosque no template — trocada pelas áreas por quiosque da
// loja (DEM-00049). ⚠️ Tem de bater com o AREA do seed da v2 e com o
// NOME_AREA_QUIOSQUE do backend (conserv-quiosques.js).
const NOME_AREA_QUIOSQUE = 'Quiosque';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const jsonAuth = () => ({ ...auth(), 'Content-Type': 'application/json' });

// Ponteiro local do rascunho em andamento (alimenta o banner "Continuar" do sub-hub).
const LS_RASCUNHO = 'conserv_rascunho_inspecao';
// Cache local do ESTADO completo do rascunho (competências + achados). Existe por
// causa de um limite deliberado do backend: no rascunho ele grava SÓ os achados
// (materializarRespostas apenasAchados), então uma área visitada SEM achado não
// fica no banco — e, na retomada, as áreas "limpas" voltariam como não visitadas.
// Este cache (mesmo navegador) devolve as marcas de visita; em outro aparelho a
// retomada degrada para "áreas que têm achado", sem quebrar. try/catch sempre.
const LS_ESTADO = 'conserv_rascunho_estado';

const inputStyle = {
  width: '100%', padding: 9, border: '1px solid #cbd5e1', borderRadius: 6,
  boxSizing: 'border-box', fontSize: 14,
};
const labelStyle = { fontSize: 12, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 };

// Valor da opção "achado fora do checklist" no seletor de item.
const ITEM_LIVRE = '__livre__';

// Normalização leve só para a PRÉVIA (contar lugares livres distintos). O número
// que vale é o do servidor; aqui basta agrupar 'DML' e 'Dml'.
const norm = (s) => String(s ?? '').trim().toLowerCase();

// Dias de conservação = DATA_CONCLUSAO − DATA_INICIO (subtração simples, em
// dias). Derivado das duas datas (decisão de 2026-07-23; antes era digitado à
// mão). Retorna null se falta alguma data ou se a conclusão é anterior ao início.
const calcularDias = (inicio, fim) => {
  if (!inicio || !fim) return null;
  const a = new Date(`${inicio}T00:00:00`);
  const b = new Date(`${fim}T00:00:00`);
  const d = Math.round((b - a) / 86400000);
  return Number.isFinite(d) && d >= 0 ? d : null;
};

// Um achado tem conteúdo? (linha aberta e não usada não é enviada nem contada.)
const achadoTemConteudo = (ac) =>
  !!(ac.id_item_template || (ac.item || '').trim() || (ac.servico || '').trim() || (ac.fotos || []).length);

// Chave estável para a lista de achados no React (não usar índice: remover uma
// linha do meio faria o React reaproveitar estado da linha errada).
let seqAchado = 0;
const novoAchado = () => ({
  key: `a${Date.now().toString(36)}_${seqAchado += 1}`,
  id_item_template: null, item: '', servico: '', especialidade: '', fotos: [],
});


const ConservacaoInspecao = ({ idTemplate, idProgramaExistente, onVoltar }) => {
  const compacto = useEhCompacto();
  const [estrutura, setEstrutura] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [gerentes, setGerentes] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [errosSubmit, setErrosSubmit] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [idPrograma, setIdPrograma] = useState(idProgramaExistente || null);
  const [recolhidas, setRecolhidas] = useState({});
  const [concluido, setConcluido] = useState(null);

  // Cabeçalho (seção 3 da especificação)
  const [cab, setCab] = useState({
    loja: '', empresa: '', cidade: '', endereco: '',
    data_inspecao: new Date().toISOString().slice(0, 10),
    data_inicio: '', data_conclusao: '', dias_totais: '',
    gerente: '', observacoes_gerais: '',
  });

  // visitadas[area] = true  → o consultor ENTROU nesta área nesta visita.
  const [visitadas, setVisitadas] = useState({});
  // naoExistentes[area] = true → a loja NÃO TEM esta área (3º estado, N/A —
  // DEM-00049). Mutuamente exclusivo com 'visitada'. Itens dela viram 'N/A':
  // fora do ICP, mas contando como olhados na Cobertura.
  const [naoExistentes, setNaoExistentes] = useState({});
  // quiosqueAreas: as áreas por quiosque da loja escolhida (DEM-00049). Vêm do
  // backend (GET /quiosques), que já devolve os itens com ids sintéticos.
  const [quiosqueAreas, setQuiosqueAreas] = useState([]);
  // achadosPorArea[area] = [{ key, id_item_template|null, item, servico, especialidade, fotos }]
  const [achadosPorArea, setAchadosPorArea] = useState({});

  // ---- carga inicial ----
  useEffect(() => {
    (async () => {
      setCarregando(true);
      try {
        const [rEstrutura, rLojas, rGerentes] = await Promise.all([
          fetch(`${API}/versao/${idTemplate}`, { headers: auth() }),
          fetch(`${API}/lojas`, { headers: auth() }),
          fetch(`${API}/gerentes`, { headers: auth() }),
        ]);
        if (!rEstrutura.ok) throw new Error('Não foi possível carregar o checklist.');
        setEstrutura(await rEstrutura.json());
        // Só as lojas das empresas que participam da Manutenção (fora: PARTNER,
        // PGO, PSM, PRT, PTN — regra única em utils/empresas.js).
        setLojas(rLojas.ok ? (await rLojas.json()).filter(ehLojaManutencao) : []);
        setGerentes(rGerentes.ok ? await rGerentes.json() : {});
      } catch (e) {
        setErro(e.message || 'Erro ao carregar.');
      } finally {
        setCarregando(false);
      }
    })();
  }, [idTemplate]);

  // ---- retomada de um programa gravado ----
  // As competências visitadas são reconstruídas do banco (área com QUALQUER
  // linha que não seja 'Fora do escopo' = visitada) e, quando houver, do cache
  // local — que preserva as áreas visitadas SEM achado, que o rascunho não grava.
  useEffect(() => {
    if (!idProgramaExistente) return;
    (async () => {
      // Cache local do mesmo navegador (superset das visitas).
      let cache = null;
      try {
        const raw = localStorage.getItem(LS_ESTADO);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && Number(parsed.id) === Number(idProgramaExistente)) cache = parsed;
      } catch { /* cache é conveniência; ausência não impede retomar */ }

      try {
        const r = await fetch(`${API}/${idProgramaExistente}`, { headers: auth() });
        if (!r.ok) return;
        const data = await r.json();
        const p = data.programa;
        setCab({
          loja: p.LOJA || '',
          // Programa antigo pode ter sido gravado sem empresa (ou com a loja
          // não mapeada) — rederiva na retomada em vez de exibir em branco.
          empresa: p.EMPRESA || (p.LOJA ? getEmpresa(p.LOJA) : ''),
          cidade: p.CIDADE || '', endereco: p.ENDERECO || '',
          data_inspecao: (p.DATA_INSPECAO || '').slice(0, 10),
          data_inicio: (p.DATA_INICIO || '').slice(0, 10),
          data_conclusao: (p.DATA_CONCLUSAO || '').slice(0, 10),
          dias_totais: p.DIAS_TOTAIS ?? '',
          gerente: p.GERENTE || '',
          observacoes_gerais: p.OBSERVACOES_GERAIS || '',
        });

        const vis = {};
        const naex = {};
        const ach = {};
        for (const i of data.itens || []) {
          const area = i.AREA_SNAPSHOT;
          const st = i.STATUS;
          // 'N/A' = área que a loja não tem (3º estado); 'Fora do escopo' = não
          // visitada; qualquer outra linha marca visita. ⚠️ O 'N/A' é excluído
          // da visita — senão a área inexistente voltaria marcada como visitada.
          if (st === 'N/A') naex[area] = true;
          else if (st && st !== 'Fora do escopo') vis[area] = true;
          // Achado = linha 🟡/🔴 (a régua binária trata as duas como pendência).
          if (st === 'Desgaste' || st === 'Necessita Intervenção') {
            (ach[area] = ach[area] || []).push({
              ...novoAchado(),
              id_item_template: i.ID_ITEM_TEMPLATE || null,
              item: i.ITEM_SNAPSHOT || '',
              servico: i.OBSERVACAO || '',
              especialidade: i.ESPECIALIDADE || '',
              fotos: i.FOTOS || [],
            });
          }
        }
        // Une as visitas do cache (áreas limpas, que o banco não guarda) com as
        // do banco. Os achados vêm do banco (têm as URLs das fotos).
        if (cache?.visitadas) {
          for (const a of Object.keys(cache.visitadas)) if (cache.visitadas[a]) vis[a] = true;
        }
        // O cache também preserva as áreas marcadas "não existe" (o rascunho não
        // grava 'N/A'; só o submit final materializa). ⚠️ Não remarcar como
        // inexistente uma área que o banco já trouxe como visitada.
        if (cache?.naoExistentes) {
          for (const a of Object.keys(cache.naoExistentes)) {
            if (cache.naoExistentes[a] && !vis[a]) naex[a] = true;
          }
        }
        setVisitadas(vis);
        setNaoExistentes(naex);
        setAchadosPorArea(ach);
      } catch { /* retomada é conveniência; falhar aqui não impede inspecionar */ }
    })();
  }, [idProgramaExistente]);

  // ---- quiosques da loja (expansão dinâmica da área genérica 'Quiosque') ----
  // Ao escolher (ou trocar) a loja, busca os quiosques dela e a tela troca a área
  // genérica pelas N áreas por quiosque. Loja sem quiosque → lista vazia → a área
  // some. Falha de rede não quebra a inspeção (degrada para sem quiosque).
  useEffect(() => {
    if (!estrutura || !cab.loja) { setQuiosqueAreas([]); return undefined; }
    let cancelado = false;
    (async () => {
      try {
        const r = await fetch(
          `${API}/quiosques/${idTemplate}/${encodeURIComponent(cab.loja)}`, { headers: auth() }
        );
        const data = r.ok ? await r.json() : null;
        if (!cancelado) setQuiosqueAreas(data?.areas || []);
      } catch {
        if (!cancelado) setQuiosqueAreas([]);
      }
    })();
    return () => { cancelado = true; };
  }, [estrutura, cab.loja, idTemplate]);

  // ---- edição das competências e dos achados ----
  const alternarVisita = (area) => setVisitadas((v) => {
    const ligado = !v[area];
    if (!ligado) {
      // Des-visitar uma área remove os achados dela — achado só existe onde se
      // entrou; deixá-los "escondidos" sumiria no submit sem aviso.
      setAchadosPorArea((a) => { const n = { ...a }; delete n[area]; return n; });
    } else {
      // Entrar numa área cancela o "não existe" (estados exclusivos: se entrei,
      // existe). O validador do backend recusaria as duas marcas juntas.
      setNaoExistentes((n) => (n[area] ? { ...n, [area]: false } : n));
    }
    return { ...v, [area]: ligado };
  });

  // 3º estado (N/A por área): "esta loja não tem esta área". Liga exclui a visita
  // e limpa os achados — não há o que inspecionar numa área que não existe.
  const alternarNaoExiste = (area) => setNaoExistentes((n) => {
    const ligado = !n[area];
    if (ligado) {
      setVisitadas((v) => (v[area] ? { ...v, [area]: false } : v));
      setAchadosPorArea((a) => { if (!a[area]) return a; const c = { ...a }; delete c[area]; return c; });
    }
    return { ...n, [area]: ligado };
  });

  const addAchado = (area) => setAchadosPorArea((a) => ({
    ...a, [area]: [...(a[area] || []), novoAchado()],
  }));
  const mudarAchado = (area, idx, campo, valor) => setAchadosPorArea((a) => ({
    ...a, [area]: a[area].map((x, i) => (i === idx ? { ...x, [campo]: valor } : x)),
  }));
  const removerAchado = (area, idx) => setAchadosPorArea((a) => ({
    ...a, [area]: a[area].filter((_, i) => i !== idx),
  }));

  // A estrutura EXIBIDA: a área genérica 'Quiosque' trocada pelas áreas por
  // quiosque da loja (ou REMOVIDA se a loja não tem quiosque). Tudo o que a tela
  // conta / renderiza / envia passa por aqui, nunca por estrutura.areas cru.
  const areasExibidas = useMemo(() => {
    const base = estrutura?.areas || [];
    if (!base.some((a) => a.area === NOME_AREA_QUIOSQUE)) return base;
    const out = [];
    for (const a of base) {
      if (a.area === NOME_AREA_QUIOSQUE) out.push(...quiosqueAreas); // [] = área some
      else out.push(a);
    }
    return out;
  }, [estrutura, quiosqueAreas]);

  const totalItens = useMemo(
    () => areasExibidas.reduce((s, a) => s + a.itens.length, 0),
    [areasExibidas]
  );
  const totalAreas = areasExibidas.length;
  const areasVisitadasCount = useMemo(
    () => areasExibidas.filter((a) => visitadas[a.area]).length,
    [areasExibidas, visitadas]
  );
  const totalAchados = useMemo(
    () => Object.values(achadosPorArea).reduce((s, l) => s + l.filter(achadoTemConteudo).length, 0),
    [achadosPorArea]
  );

  // ---- prévia do ICP + cobertura (espelha calcularIcp/calcularCobertura) ----
  // ICP binário: itens SEM achado ÷ (itens olhados + lugares livres com achado).
  // Cobertura: itens de áreas visitadas ÷ total do checklist. Prévia — o oficial
  // é o do servidor.
  const previa = useMemo(() => {
    if (!estrutura) return { icp: null, cobertura: null, olhados: 0 };
    const areasVis = areasExibidas.filter((a) => visitadas[a.area]);
    const olhados = areasVis.reduce((s, a) => s + a.itens.length, 0);
    // N/A por área conta como OLHADO na Cobertura ("fui lá e não existe"), mas
    // fica FORA do ICP. Soma os itens das áreas inexistentes só ao numerador da
    // cobertura, sem tocar no denominador do ICP.
    const itensNA = areasExibidas
      .filter((a) => naoExistentes[a.area] && !visitadas[a.area])
      .reduce((s, a) => s + a.itens.length, 0);
    const idsComAchado = new Set();
    const lugaresLivres = new Set();
    for (const a of areasVis) {
      for (const ac of achadosPorArea[a.area] || []) {
        if (!achadoTemConteudo(ac)) continue;
        if (ac.id_item_template) idsComAchado.add(Number(ac.id_item_template));
        else lugaresLivres.add(`${norm(a.area)}|${norm(ac.item)}`);
      }
    }
    const denom = olhados + lugaresLivres.size;
    const cobertura = totalItens > 0
      ? Number((((olhados + itensNA) / totalItens) * 100).toFixed(2)) : null;
    // ICP nulo quando nada pontua (nenhuma área visitada); a cobertura ainda
    // reflete o que foi marcado N/A.
    if (denom === 0) return { icp: null, cobertura, olhados: 0 };
    const semAchado = olhados - idsComAchado.size;
    const icp = Number((((semAchado) / denom) * 100).toFixed(2));
    return { icp, cobertura, olhados };
  }, [estrutura, areasExibidas, visitadas, naoExistentes, achadosPorArea, totalItens]);

  // ---- montagem do payload (contrato NOVO: competencias + achados) ----
  const montarPayload = useCallback((rascunho) => {
    const competencias = [];
    const achados = [];
    for (const area of areasExibidas) {
      if (!visitadas[area.area]) continue;
      competencias.push(area.area);
      for (const ac of achadosPorArea[area.area] || []) {
        if (!achadoTemConteudo(ac)) continue;
        // Item do checklist manda o id (o servidor usa o snapshot do template);
        // achado livre manda o nome digitado, sem id.
        const doTemplate = ac.id_item_template
          ? area.itens.find((i) => i.ID === Number(ac.id_item_template))
          : null;
        achados.push({
          id_item_template: ac.id_item_template || null,
          area: area.area,
          item: doTemplate ? doTemplate.ITEM : (ac.item || '').trim(),
          servico: ac.servico || null,
          especialidade: ac.especialidade || null,
          fotos: ac.fotos || [],
        });
      }
    }
    // Áreas que a loja NÃO tem (N/A por área) — não visitadas e marcadas
    // inexistentes. O backend grava 'N/A' nos itens delas.
    const competencias_nao_existentes = areasExibidas
      .filter((area) => naoExistentes[area.area] && !visitadas[area.area])
      .map((area) => area.area);
    return {
      ...cab,
      // Derivado das datas — não vem mais de um campo digitado.
      dias_totais: calcularDias(cab.data_inicio, cab.data_conclusao),
      id_template: idTemplate,
      id_programa: idPrograma || undefined,
      competencias,
      competencias_nao_existentes,
      achados,
      rascunho,
    };
  }, [areasExibidas, visitadas, naoExistentes, achadosPorArea, cab, idTemplate, idPrograma]);

  // Grava o ponteiro (banner "Continuar") + o cache do estado completo, que
  // preserva as áreas visitadas sem achado na retomada no mesmo navegador.
  const guardarPonteiro = (id) => {
    try {
      localStorage.setItem(LS_RASCUNHO, JSON.stringify({
        id, idTemplate, loja: cab.loja, em: new Date().toISOString(),
      }));
      localStorage.setItem(LS_ESTADO, JSON.stringify({
        id, idTemplate, visitadas, naoExistentes, em: new Date().toISOString(),
      }));
    } catch { /* localStorage cheio/bloqueado não pode derrubar o save */ }
  };
  const limparPonteiro = () => {
    try { localStorage.removeItem(LS_RASCUNHO); localStorage.removeItem(LS_ESTADO); } catch { /* idem */ }
  };

  // ---------------------------------------------------------------------
  // POST do formulário, com RECUPERAÇÃO de rascunho apagado.
  //
  // O payload leva `id_programa` para atualizar o mesmo registro criado pelo
  // autosave. Se esse registro sumiu do banco (Admin cancelou/apagou, limpeza
  // de teste), o backend devolve 404 e, sem tratamento, a tela entra em beco
  // sem saída: toda gravação falha e a inspeção inteira se perde. O 404 é
  // tratado como "o destino não existe mais": descarta o ponteiro órfão e
  // reenvia UMA vez como programa NOVO (não há o que duplicar). Mesmo tratamento
  // da Certificação.
  // ---------------------------------------------------------------------
  const postar = useCallback(async (rascunho) => {
    const enviarCom = (comId) => {
      const corpo = montarPayload(rascunho);
      if (!comId) corpo.id_programa = undefined;
      return fetch(`${API}/submit`, { method: 'POST', headers: jsonAuth(), body: JSON.stringify(corpo) });
    };
    let r = await enviarCom(true);
    let data = await r.json();
    if (r.status === 404 && idPrograma) {
      limparPonteiro();
      setIdPrograma(null);
      r = await enviarCom(false);
      data = await r.json();
    }
    return { r, data };
  }, [montarPayload, idPrograma]);

  // Autosave: grava o rascunho em silêncio (sem validar, sem tela de erro).
  // Lança em caso de falha — quem mostra o estado é o hook.
  const salvarRascunhoAuto = async () => {
    const { r, data } = await postar(true);
    if (!r.ok) throw new Error(data?.message || 'Erro ao salvar rascunho.');
    if (data.id && data.id !== idPrograma) setIdPrograma(data.id);
    guardarPonteiro(data.id);
  };

  // Snapshot do conteúdo — o autosave só dispara quando esta string muda.
  const snapshot = useMemo(
    () => JSON.stringify({ cab, visitadas, naoExistentes, achadosPorArea }),
    [cab, visitadas, naoExistentes, achadosPorArea]
  );
  // Ligado só depois de carregar, com a LOJA escolhida (é o mínimo para o
  // registro fazer sentido) e enquanto a inspeção não foi concluída.
  const autosaveAtivo = !carregando && !!cab.loja && !concluido;
  const { estado: autosaveEstado } = useAutosaveRascunho({
    ativo: autosaveAtivo, snapshot, salvarFn: salvarRascunhoAuto,
  });

  // Gravação explícita (botão): mostra erro na tela, ao contrário do autosave.
  const enviar = async (rascunho) => {
    setSalvando(true); setErro(''); setErrosSubmit([]);
    try {
      const { r, data } = await postar(rascunho);
      if (!r.ok) {
        setErro(data?.message || 'Erro ao gravar.');
        setErrosSubmit(data?.erros || []);
        // Rola ao topo: com muitas áreas, o erro no rodapé passa despercebido.
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setIdPrograma(data.id);
      if (rascunho) {
        guardarPonteiro(data.id);
      } else {
        limparPonteiro();
        setConcluido(data);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      setErro('Erro de rede ao gravar.');
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) return <p style={{ color: '#64748b' }}>Carregando o checklist…</p>;
  if (!estrutura) {
    return (
      <div>
        <button onClick={onVoltar} style={botaoVoltar}><ArrowLeft size={16} /> Voltar</button>
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginTop: 12 }}>{erro}</div>
      </div>
    );
  }

  // Tela de conclusão — fecha o ciclo mostrando o ICP e a COBERTURA OFICIAIS.
  if (concluido) {
    const parcial = concluido.inspecaoParcial;
    return (
      <div style={{ maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
        <CheckCircle2 size={56} color="#059669" />
        <h3 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: '16px 0 8px' }}>Inspeção concluída</h3>
        <p style={{ color: '#64748b', margin: '0 0 24px' }}>
          {cab.loja} · {cab.data_inspecao}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ padding: 20, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>ICP</div>
            <div style={{ fontSize: 40, fontWeight: 800, color: '#4f46e5', fontVariantNumeric: 'tabular-nums' }}>
              {Number(concluido.icp).toFixed(2)}%
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{concluido.classificacaoIcp}</div>
          </div>
          <div style={{
            padding: 20, borderRadius: 12,
            background: parcial ? '#fffbeb' : '#f0fdf4',
            border: `1px solid ${parcial ? '#fde68a' : '#bbf7d0'}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Cobertura</div>
            <div style={{ fontSize: 40, fontWeight: 800, color: parcial ? '#b45309' : '#059669', fontVariantNumeric: 'tabular-nums' }}>
              {concluido.cobertura === null || concluido.cobertura === undefined ? '—' : `${Number(concluido.cobertura).toFixed(0)}%`}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>
              {concluido.itensAvaliados ?? '—'} de {concluido.itensChecklist ?? '—'} itens olhados
            </div>
          </div>
        </div>
        {parcial && (
          <p style={{ color: '#b45309', fontSize: 12.5, margin: '12px 0 0', fontWeight: 600 }}>
            Inspeção parcial — o ICP acima vale para o que foi olhado, não para o prédio inteiro.
          </p>
        )}
        {/* As OS nascem aqui, na finalização — o número vem do servidor.
            (A troca por OS avulsa é a Fase B da DEM-00025.) */}
        {concluido.ordensServico > 0 ? (
          <div style={{
            margin: '20px 0', padding: 16, background: '#eef2ff',
            border: '1px solid #c7d2fe', borderRadius: 12, color: '#3730a3',
          }}>
            <strong style={{ fontSize: 16 }}>
              {concluido.ordensServico} Ordem(ns) de Serviço geradas
            </strong>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              Uma para cada não conformidade. Acompanhe a execução em
              <strong> Programas gravados → abrir o programa → Ordens de Serviço</strong>.
            </div>
          </div>
        ) : (
          <p style={{ color: '#059669', fontSize: 13, margin: '20px 0', fontWeight: 600 }}>
            Nenhuma não conformidade registrada — não há Ordem de Serviço a executar.
          </p>
        )}
        <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 24px' }}>
          A inspeção está fechada. Para corrigi-la, cancele o programa e faça uma nova.
        </p>
        <button onClick={onVoltar} style={{
          padding: '12px 24px', background: '#4f46e5', color: '#fff', border: 'none',
          borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 15,
        }}>
          Voltar ao início
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* ---------------- barra fixa: progresso + ICP + ações ---------------- */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20, background: '#fff',
        borderBottom: '1px solid #e2e8f0', padding: '12px 0', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <button onClick={onVoltar} style={botaoVoltar}><ArrowLeft size={16} /> Voltar</button>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          <strong style={{ color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
            {areasVisitadasCount}/{totalAreas}
          </strong> áreas visitadas
          <span style={{ margin: '0 6px', color: '#cbd5e1' }}>·</span>
          <strong style={{ color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{totalAchados}</strong> não conformidade(s)
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px',
          background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 999,
        }}>
          <Gauge size={16} color="#4338ca" />
          <span style={{ fontSize: 12, fontWeight: 800, color: '#4338ca' }}>ICP (prévia)</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: '#312e81', fontVariantNumeric: 'tabular-nums' }}>
            {previa.icp === null ? '—' : `${previa.icp.toFixed(2)}%`}
          </span>
          {previa.icp !== null && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#4338ca' }}>{classificarIcp(previa.icp)}</span>
          )}
          {previa.cobertura !== null && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
              · cobertura {previa.cobertura.toFixed(0)}%
            </span>
          )}
        </div>
        {/* Estado do autosave — só aparece quando ele está ligado (loja escolhida). */}
        {autosaveAtivo && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700,
            color: autosaveEstado === 'erro' ? '#b91c1c' : autosaveEstado === 'salvando' ? '#64748b' : '#059669',
          }}>
            {autosaveEstado === 'salvando' ? <><Cloud size={14} /> Salvando rascunho…</>
              : autosaveEstado === 'salvo' ? <><Check size={14} /> Rascunho salvo</>
                : autosaveEstado === 'erro' ? <><AlertTriangle size={14} /> Falha ao salvar rascunho</>
                  : null}
          </span>
        )}
        {/* No celular os dois botões ocupam a linha inteira (flex:1) para virarem
            alvos de toque confortáveis, em vez de espremidos no canto. */}
        <div style={{ marginLeft: compacto ? 0 : 'auto', display: 'flex', gap: 8, ...(compacto ? { width: '100%' } : {}) }}>
          <button onClick={() => enviar(true)} disabled={salvando} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 16px',
            background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
            borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: salvando ? 0.6 : 1,
            ...(compacto ? { flex: 1 } : {}),
          }}>
            {salvando ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Salvar rascunho
          </button>
          <button onClick={() => enviar(false)} disabled={salvando} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 16px',
            background: '#4f46e5', color: '#fff', border: 'none',
            borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: salvando ? 0.6 : 1,
            ...(compacto ? { flex: 1 } : {}),
          }}>
            <Send size={16} /> Concluir inspeção
          </button>
        </div>
      </div>

      {erro && (
        <div style={{ padding: 14, background: '#fee2e2', color: '#991b1b', borderRadius: 10, marginBottom: 16 }}>
          <strong>{erro}</strong>
          {errosSubmit.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13 }}>
              {errosSubmit.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* ---------------- como preencher (uma linha, sem manual) ---------------- */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', marginBottom: 16,
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, fontSize: 13, color: '#1e40af',
      }}>
        <MapPin size={16} style={{ marginTop: 1, flexShrink: 0 }} />
        <span>
          Marque <strong>as áreas em que você entrou</strong> e registre só as <strong>não conformidades</strong> (o que precisa de reparo).
          O que você olhou e está ok conta como conforme automaticamente; área não marcada fica fora da cobertura.
        </span>
      </div>

      {/* ---------------- cabeçalho (dados gerais) ---------------- */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Dados gerais</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
          <div>
            <label style={labelStyle}>Restaurante *</label>
            {/* Trocar a loja recalcula a EMPRESA (é propriedade da loja) e zera
                o gerente (o da loja anterior não vale para esta). */}
            <select value={cab.loja}
              onChange={(e) => {
                const loja = e.target.value;
                setCab({ ...cab, loja, empresa: loja ? getEmpresa(loja) : '', gerente: '' });
              }}
              style={inputStyle}>
              <option value="">— escolher —</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Gerente</label>
            {/* Lista vem da dtbConfere e depende da loja — por isso trocar a
                loja zera este campo (o gerente da loja anterior não vale). */}
            <select value={cab.gerente} onChange={(e) => setCab({ ...cab, gerente: e.target.value })}
              style={inputStyle} disabled={!cab.loja}>
              <option value="">{cab.loja ? '— escolher —' : 'escolha a loja primeiro'}</option>
              {(gerentes[cab.loja] || []).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Data da inspeção *</label>
            <input type="date" value={cab.data_inspecao} onChange={(e) => setCab({ ...cab, data_inspecao: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Empresa</label>
            {/* Somente leitura: vem do mapa loja → empresa (src/utils/empresas.js),
                a mesma fonte que a Pesquisa de Clima usa. Campo livre aqui
                produziria 'P&M', 'PM' e 'P & M' na mesma coluna — e quebraria
                qualquer agrupamento por empresa no relatório e no BI. */}
            <input value={cab.empresa} readOnly disabled
              placeholder={cab.loja ? '' : 'definida pela loja'}
              title="Definida automaticamente pela loja escolhida"
              style={{ ...inputStyle, background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' }} />
          </div>
          <div>
            <label style={labelStyle}>Cidade</label>
            <input value={cab.cidade} onChange={(e) => setCab({ ...cab, cidade: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Endereço</label>
            <input value={cab.endereco} onChange={(e) => setCab({ ...cab, endereco: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Início da conservação</label>
            <input type="date" value={cab.data_inicio} onChange={(e) => setCab({ ...cab, data_inicio: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Conclusão</label>
            <input type="date" value={cab.data_conclusao} onChange={(e) => setCab({ ...cab, data_conclusao: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Dias de conservação</label>
            {/* Derivado das datas (conclusão − início), somente leitura. */}
            <input type="text" readOnly disabled
              value={calcularDias(cab.data_inicio, cab.data_conclusao) ?? ''}
              placeholder="preencha início e conclusão"
              title="Calculado automaticamente: conclusão − início"
              style={{ ...inputStyle, background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' }} />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={labelStyle}>Observações gerais</label>
          <textarea value={cab.observacoes_gerais} onChange={(e) => setCab({ ...cab, observacoes_gerais: e.target.value })}
            rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      </div>

      {/* ---------------- áreas (competências) ---------------- */}
      {areasExibidas.map((area) => {
        const visitada = !!visitadas[area.area];
        const naoExiste = !!naoExistentes[area.area];
        const recolhida = recolhidas[area.area];
        const achados = achadosPorArea[area.area] || [];
        const achadosValidos = achados.filter(achadoTemConteudo).length;
        return (
          <div key={area.area} style={{
            background: '#fff',
            border: `1px solid ${visitada ? '#c7d2fe' : (naoExiste ? '#fcd34d' : '#e2e8f0')}`,
            borderRadius: 12, marginBottom: 16, overflow: 'hidden',
          }}>
            {/* Cabeçalho da área: recolher + toggles "entrei aqui" / "não existe" + contagem de achados. */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
              background: visitada ? '#eef2ff' : (naoExiste ? '#fffbeb' : '#f8fafc'),
              borderBottom: ((visitada && !recolhida) || naoExiste) ? '1px solid #e2e8f0' : 'none',
            }}>
              <button onClick={() => setRecolhidas((r) => ({ ...r, [area.area]: !r[area.area] }))}
                title={recolhida ? 'Expandir' : 'Recolher'}
                style={{ display: 'inline-flex', padding: 2, background: 'none', border: 'none', cursor: 'pointer' }}>
                {recolhida ? <ChevronRight size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
              </button>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', flex: 1 }}>{area.area}</span>
              <span style={{ fontSize: 12, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                {area.itens.length} {area.itens.length === 1 ? 'item' : 'itens'}
              </span>
              {visitada && achadosValidos > 0 && (
                <span style={{
                  padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                  background: '#fee2e2', color: '#b91c1c',
                }}>
                  {achadosValidos} não conformidade(s)
                </span>
              )}
              {/* O toggle "Entrei nesta área" é o ato que decide a cobertura. */}
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                border: `1px solid ${visitada ? '#4f46e5' : '#cbd5e1'}`,
                background: visitada ? '#4f46e5' : '#fff', color: visitada ? '#fff' : '#64748b',
              }}>
                <input type="checkbox" checked={visitada} onChange={() => alternarVisita(area.area)}
                  style={{ width: 15, height: 15, accentColor: '#fff', cursor: 'pointer' }} />
                {visitada ? 'Entrei nesta área' : 'Entrei aqui?'}
              </label>
              {/* N/A por área: "a loja não tem esta área". Sai do ICP e NÃO
                  derruba a cobertura. Só aparece enquanto a área não foi visitada
                  (os dois estados são exclusivos). */}
              {!visitada && (
                <label title="A loja não tem esta área — fica fora do ICP e não derruba a cobertura" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  border: `1px solid ${naoExiste ? '#d97706' : '#cbd5e1'}`,
                  background: naoExiste ? '#f59e0b' : '#fff', color: naoExiste ? '#fff' : '#64748b',
                }}>
                  <input type="checkbox" checked={naoExiste} onChange={() => alternarNaoExiste(area.area)}
                    style={{ width: 15, height: 15, accentColor: '#fff', cursor: 'pointer' }} />
                  {naoExiste ? 'Não existe nesta loja' : 'Não existe aqui?'}
                </label>
              )}
            </div>

            {/* Área marcada como inexistente: seus itens viram N/A. */}
            {naoExiste && (
              <div style={{ padding: '10px 16px', fontSize: 13, color: '#92400e', background: '#fffbeb' }}>
                Área marcada como <strong>inexistente nesta loja</strong> — seus {area.itens.length} itens
                ficam como <strong>N/A</strong> (fora do ICP e sem derrubar a cobertura).
              </div>
            )}

            {visitada && !recolhida && (
              <div style={{ padding: 14 }}>
                {achados.length === 0 ? (
                  <p style={{ margin: '4px 0 12px', fontSize: 13, color: '#64748b' }}>
                    Nenhuma não conformidade nesta área — os {area.itens.length} itens contam como conformes.
                    Registre uma não conformidade só se houver algo a reparar.
                  </p>
                ) : (
                  achados.map((ac, idx) => (
                    <AchadoLinha
                      key={ac.key}
                      achado={ac}
                      itens={area.itens}
                      especialidades={estrutura.especialidades}
                      maxFotos={estrutura.maxFotosItem}
                      loja={cab.loja}
                      area={area.area}
                      compacto={compacto}
                      onMudar={(campo, valor) => mudarAchado(area.area, idx, campo, valor)}
                      onRemover={() => removerAchado(area.area, idx)}
                    />
                  ))
                )}

                <button onClick={() => addAchado(area.area)} style={{
                  marginTop: achados.length ? 12 : 0, display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', background: '#fff', color: '#b91c1c',
                  border: '1px dashed #fca5a5', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13,
                }}>
                  <Plus size={15} /> Registrar não conformidade
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------
// Um achado: item (do checklist ou livre) + serviço + especialidade + foto.
// Vale para os dois casos porque a diferença é só de onde vem o "item":
// um <select> do checklist da área, ou "Outro" com nome digitado.
// ---------------------------------------------------------------------
const AchadoLinha = ({ achado, itens, especialidades, maxFotos, loja, area, compacto, onMudar, onRemover }) => {
  const ehLivre = !achado.id_item_template;
  return (
    <div style={{
      marginBottom: 12, padding: 12, background: '#fff7ed',
      border: '1px solid #fed7aa', borderRadius: 8, display: 'grid', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: '#c2410c' }}>Não conformidade</span>
        <button onClick={onRemover} title="Remover"
          style={{
            marginLeft: 'auto', display: 'inline-flex', padding: 4, cursor: 'pointer',
            border: '1px solid #fed7aa', background: '#fff', color: '#c2410c', borderRadius: 6,
          }}>
          <Trash2 size={14} />
        </button>
      </div>

      {/* Item: escolha no checklist da área ou "Outro" (achado fora da lista). */}
      <div style={{ display: 'grid', gap: 8 }}>
        <div>
          <label style={labelStyle}>Item *</label>
          <select
            value={ehLivre ? ITEM_LIVRE : String(achado.id_item_template)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === ITEM_LIVRE) { onMudar('id_item_template', null); onMudar('item', ''); }
              else {
                const it = itens.find((i) => String(i.ID) === v);
                onMudar('id_item_template', Number(v));
                onMudar('item', it ? it.ITEM : '');
              }
            }}
            style={inputStyle}>
            <option value={ITEM_LIVRE}>— Outro (fora do checklist) —</option>
            {itens.map((i) => <option key={i.ID} value={String(i.ID)}>{i.ITEM}</option>)}
          </select>
        </div>
        {ehLivre && (
          <input value={achado.item} onChange={(e) => onMudar('item', e.target.value)}
            placeholder="O que foi encontrado (título curto) *" style={inputStyle} />
        )}
      </div>

      <textarea value={achado.servico} rows={2}
        onChange={(e) => onMudar('servico', e.target.value)}
        placeholder="Serviço a executar — descreva o problema/reparo *"
        style={{ ...inputStyle, resize: 'vertical' }} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={achado.especialidade} onChange={(e) => onMudar('especialidade', e.target.value)}
          style={{ ...inputStyle, ...(compacto ? { flex: '1 1 100%' } : { width: 'auto', minWidth: 200 }) }}>
          <option value="">Especialidade (o sistema sugere pela descrição)</option>
          {especialidades.map((e2) => <option key={e2} value={e2}>{e2}</option>)}
        </select>
        <FotosItem fotos={achado.fotos || []} loja={loja} area={area} obrigatoria
          max={maxFotos}
          onChange={(fotos) => onMudar('fotos', fotos)} />
      </div>
    </div>
  );
};

const botaoVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

export default ConservacaoInspecao;
